import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Divider, Avatar, Skeleton, CircularProgress, Tooltip, IconButton, TextField } from "@mui/material";
import Icon from "@mui/material/Icon";
import { Doughnut, Bar } from "react-chartjs-2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { fetchCostExplorer } from "store/slices/costExplorerSlice";
import { EmptyState } from "../components";

// ─── Env-based resource detection ────────────────────────────────────────────
function getResource() {
  const url = process.env.REACT_APP_CONSOLE_API || "";
  if (url.includes("/prod") || url.includes("o6zei5oc56")) return "vivre-prod";
  if (url.includes("dish")) return "vivre-dish";
  return "vivre-dev";
}
const RESOURCE = getResource();

const COLORS = [
  "#00ACC4","#2F36CB","#27AE60","#F39C12","#E74C3C",
  "#9B59B6","#1ABC9C","#E67E22","#3498DB","#2ECC71",
  "#F1C40F","#E91E63","#00BCD4","#8BC34A","#FF5722",
];

// Format date to DD-MM-YYYY for API
function toApiDate(dateStr) {
  if (!dateStr) return undefined;
  // dateStr is YYYY-MM-DD from input[type=date]
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
}

// Get first day of current month as YYYY-MM-DD
function getMonthStart() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

// Get today as YYYY-MM-DD
function getToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function fmt(amount) {
  if (!amount || amount <= 0) return "$0.00";
  if (amount >= 1000) return `$${amount.toFixed(2)}`;
  if (amount >= 1) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(6)}`;
}

function extractName(arn) {
  if (!arn) return arn;
  const m = arn.match(/\/aws\/lambda\/[^-]+-[^-]+-(.+)$/);
  if (m) return m[1];
  const parts = arn.split("/");
  return parts[parts.length - 1] || arn;
}

function calcSummary(services) {
  let tax = 0, lateFee = 0, usage = 0;
  (services || []).forEach((s) => {
    if (s.amount <= 0) return;
    if (s.service === "Tax") { tax += s.amount; return; }
    if (s.service === "OCBLateFee") { lateFee += s.amount; return; }
    usage += s.amount;
  });
  return { total: usage + tax + lateFee, usage, tax, lateFee };
}

function CostBilling() {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.costExplorer);
  const [startDate, setStartDate] = useState(getMonthStart());
  const [endDate, setEndDate] = useState(getToday());

  const fetchData = useCallback(() => {
    dispatch(fetchCostExplorer({
      resource: RESOURCE,
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
    }));
  }, [dispatch, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resp = data?.data;
  const services = resp?.serviceWiseCost || [];
  const resources = resp?.resourceWiseCost || [];
  const meta = resp?.resourceWiseCostMeta;

  const summary = useMemo(() => calcSummary(services), [services]);

  const pieItems = useMemo(() =>
    services.filter((s) => s.amount > 0 && s.service !== "Tax" && s.service !== "OCBLateFee")
      .sort((a, b) => b.amount - a.amount), [services]);

  const top10 = useMemo(() => pieItems.slice(0, 10), [pieItems]);

  const top15Resources = useMemo(() =>
    resources.filter((r) => r.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 15),
    [resources]);

  // Doughnut config with overflow-safe tooltip
  const doughnut = useMemo(() => {
    if (!pieItems.length) return null;
    return {
      data: {
        labels: pieItems.map((s) => s.service),
        datasets: [{
          data: pieItems.map((s) => s.amount),
          backgroundColor: COLORS.slice(0, pieItems.length),
          borderWidth: 2, borderColor: "#fff", hoverOffset: 8,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: "62%",
        plugins: {
          legend: { display: false },
          tooltip: {
            position: "nearest",
            cornerRadius: 8, padding: 12,
            backgroundColor: "rgba(20,29,70,0.95)",
            titleFont: { size: 12, weight: "600" },
            bodyFont: { size: 11 },
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed || 0;
                const t = ctx.dataset.data.reduce((a, b) => a + b, 0);
                return ` ${fmt(v)} (${t > 0 ? ((v / t) * 100).toFixed(1) : 0}%)`;
              },
            },
          },
        },
      },
    };
  }, [pieItems]);

  // Horizontal bar — top services
  const barCfg = useMemo(() => {
    if (!top10.length) return null;
    return {
      data: {
        labels: top10.map((s) => s.service),
        datasets: [{
          data: top10.map((s) => s.amount),
          backgroundColor: COLORS.slice(0, top10.length),
          borderRadius: 6, maxBarThickness: 22,
        }],
      },
      options: {
        indexAxis: "y", responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            cornerRadius: 8, padding: 10,
            backgroundColor: "rgba(20,29,70,0.95)",
            callbacks: { label: (ctx) => ` ${fmt(ctx.parsed.x)}` },
          },
        },
        scales: {
          x: { grid: { drawBorder: false, color: "#E8EDF2" }, ticks: { font: { size: 10 }, color: "#5D6D7E" } },
          y: { grid: { display: false }, ticks: { font: { size: 11 }, color: "#141D46", autoSkip: false } },
        },
      },
    };
  }, [top10]);

  // Horizontal bar — top resources
  const resCfg = useMemo(() => {
    if (!top15Resources.length) return null;
    return {
      data: {
        labels: top15Resources.map((r) => extractName(r.resource)),
        datasets: [{
          data: top15Resources.map((r) => r.amount),
          backgroundColor: "rgba(47,54,203,0.75)",
          borderRadius: 5, maxBarThickness: 18,
        }],
      },
      options: {
        indexAxis: "y", responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            cornerRadius: 8, padding: 10,
            backgroundColor: "rgba(20,29,70,0.95)",
            callbacks: {
              title: (ctx) => top15Resources[ctx[0]?.dataIndex]?.resource || "",
              label: (ctx) => ` $${ctx.parsed.x.toFixed(6)}`,
            },
          },
        },
        scales: {
          x: { grid: { drawBorder: false, color: "#E8EDF2" }, ticks: { font: { size: 10 }, color: "#5D6D7E" } },
          y: { grid: { display: false }, ticks: { font: { size: 10 }, color: "#141D46", autoSkip: false } },
        },
      },
    };
  }, [top15Resources]);

  // Table data
  const svcTable = useMemo(() => ({
    columns: [
      { Header: "Service", accessor: "service", width: "40%" },
      { Header: "Cost (USD)", accessor: "cost", align: "right",
        Cell: ({ value }) => <MDTypography variant="caption" fontWeight="bold" color="dark">{fmt(value)}</MDTypography> },
      { Header: "Share", accessor: "pct", align: "right",
        Cell: ({ value }) => (
          <MDBox display="flex" alignItems="center" gap={1} justifyContent="flex-end">
            <MDBox sx={{ width: 36, height: 5, borderRadius: 3, bgcolor: "#F4F6F9", overflow: "hidden" }}>
              <MDBox sx={{ width: `${Math.min(value, 100)}%`, height: "100%", borderRadius: 3, bgcolor: "#00ACC4" }} />
            </MDBox>
            <MDTypography variant="caption" color="secondary" sx={{ fontSize: 11 }}>{value}%</MDTypography>
          </MDBox>
        ),
      },
      { Header: "Unit", accessor: "unit", align: "center" },
    ],
    rows: pieItems.map((s) => ({
      service: s.service,
      cost: s.amount,
      pct: summary.usage > 0 ? parseFloat(((s.amount / summary.usage) * 100).toFixed(2)) : 0,
      unit: s.unit,
    })),
  }), [pieItems, summary]);

  const resTable = useMemo(() => ({
    columns: [
      { Header: "Resource", accessor: "name", width: "25%" },
      { Header: "ARN", accessor: "arn", width: "35%",
        Cell: ({ value }) => (
          <Tooltip title={value} arrow placement="top">
            <span style={{ fontSize: 11, fontFamily: "monospace", color: "#5D6D7E", maxWidth: 240, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
          </Tooltip>
        ),
      },
      { Header: "Service", accessor: "service" },
      { Header: "Cost (USD)", accessor: "cost", align: "right",
        Cell: ({ value }) => <MDTypography variant="caption" fontWeight="bold" color="dark">{fmt(value)}</MDTypography> },
    ],
    rows: resources.filter((r) => r.amount > 0).map((r) => ({
      name: extractName(r.resource), arn: r.resource, service: r.service, cost: r.amount,
    })),
  }), [resources]);

  // ─── Error State ─────────────────────────────────────────────────────────────
  if (error && !loading && !data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} px={3}>
          <Card sx={{ border: "1px solid #E8EDF2", borderRadius: "16px" }}>
            <MDBox p={5} textAlign="center">
              <Icon sx={{ fontSize: "48px !important", color: "#E74C3C", mb: 1 }}>error_outline</Icon>
              <MDTypography variant="h6" color="dark" mb={1}>Failed to load cost data</MDTypography>
              <MDTypography variant="body2" color="secondary" mb={2}>{error}</MDTypography>
              <MDButton variant="gradient" color="info" size="small" onClick={fetchData}>
                <Icon>refresh</Icon>&nbsp;Retry
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Header */}
        <MDBox mb={3}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <MDTypography variant="caption" color="secondary">Control Plane</MDTypography>
            <MDTypography variant="caption" color="secondary">›</MDTypography>
            <MDTypography variant="caption" fontWeight="bold" color="dark">Cost & Billing</MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold" color="dark">AWS Cost Dashboard</MDTypography>
              <MDTypography variant="caption" color="secondary">
                Resource: {RESOURCE}
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <TextField
                type="date"
                size="small"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 155, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 155, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <IconButton onClick={fetchData} disabled={loading} sx={{ border: "1px solid #E8EDF2", borderRadius: "10px", p: 1 }}>
                {loading ? <CircularProgress size={18} /> : <Icon>refresh</Icon>}
              </IconButton>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Loading skeleton */}
        {loading && !data && (
          <>
            <Grid container spacing={2.5} mb={4}>
              {[1,2,3,4].map((i) => (
                <Grid item xs={12} sm={6} lg={3} key={i}>
                  <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "16px" }} />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={360} sx={{ borderRadius: "16px" }} /></Grid>
              <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={360} sx={{ borderRadius: "16px" }} /></Grid>
            </Grid>
          </>
        )}

        {/* Empty */}
        {!loading && data && pieItems.length === 0 && (
          <Card sx={{ border: "1px solid #E8EDF2", borderRadius: "16px" }}>
            <EmptyState message="No cost data available for the selected date range." icon="inbox" />
          </Card>
        )}

        {/* Main content */}
        {data && pieItems.length > 0 && (
          <>
            {/* KPI Cards */}
            <Grid container spacing={2.5} mb={4}>
              <Grid item xs={12} sm={6} lg={3}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px", transition: "box-shadow .2s, transform .2s", "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,.07)", transform: "translateY(-2px)" } }}>
                  <MDBox p={2.5} display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 50, height: 50, background: "linear-gradient(135deg,#2F36CB,#00ACC4)", borderRadius: "14px", boxShadow: "0 4px 12px #2F36CB30" }}>
                      <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>account_balance_wallet</Icon>
                    </Avatar>
                    <MDBox>
                      <MDTypography variant="caption" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: .8, fontSize: 10 }}>Total Charges</MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ lineHeight: 1.2 }}>{fmt(summary.total)}</MDTypography>
                      <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10 }}>All inclusive</MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px", transition: "box-shadow .2s, transform .2s", "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,.07)", transform: "translateY(-2px)" } }}>
                  <MDBox p={2.5} display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 50, height: 50, background: "linear-gradient(135deg,#00ACC4,#1ABC9C)", borderRadius: "14px", boxShadow: "0 4px 12px #00ACC440" }}>
                      <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>cloud</Icon>
                    </Avatar>
                    <MDBox>
                      <MDTypography variant="caption" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: .8, fontSize: 10 }}>AWS Usage</MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ lineHeight: 1.2 }}>{fmt(summary.usage)}</MDTypography>
                      <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10 }}>Excludes Tax & Late Fee</MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px", transition: "box-shadow .2s, transform .2s", "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,.07)", transform: "translateY(-2px)" } }}>
                  <MDBox p={2.5} display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 50, height: 50, background: "linear-gradient(135deg,#F39C12,#E67E22)", borderRadius: "14px", boxShadow: "0 4px 12px #F39C1240" }}>
                      <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>receipt</Icon>
                    </Avatar>
                    <MDBox>
                      <MDTypography variant="caption" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: .8, fontSize: 10 }}>Tax</MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ lineHeight: 1.2 }}>{fmt(summary.tax)}</MDTypography>
                      <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10 }}>Applied taxes</MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px", transition: "box-shadow .2s, transform .2s", "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,.07)", transform: "translateY(-2px)" } }}>
                  <MDBox p={2.5} display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 50, height: 50, background: "linear-gradient(135deg,#E74C3C,#C0392B)", borderRadius: "14px", boxShadow: "0 4px 12px #E74C3C40" }}>
                      <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>warning_amber</Icon>
                    </Avatar>
                    <MDBox>
                      <MDTypography variant="caption" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: .8, fontSize: 10 }}>Late Fee</MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ lineHeight: 1.2 }}>{fmt(summary.lateFee)}</MDTypography>
                      <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10 }}>OCB charges</MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3} mb={4}>
              {/* Doughnut */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px", overflow: "visible" }}>
                  <MDBox px={2.5} py={2}>
                    <MDTypography variant="h6" fontWeight="bold" color="dark">Service Cost Distribution</MDTypography>
                  </MDBox>
                  <Divider sx={{ m: 0 }} />
                  <MDBox p={2.5} display="flex" alignItems="center" gap={3} flexWrap="wrap" sx={{ minHeight: 280 }}>
                    <MDBox sx={{ width: 200, height: 200, flexShrink: 0, position: "relative", overflow: "visible" }}>
                      {doughnut && <Doughnut data={doughnut.data} options={doughnut.options} />}
                    </MDBox>
                    <MDBox flex={1} sx={{ maxHeight: 240, overflowY: "auto", pr: 1 }}>
                      {pieItems.slice(0, 12).map((item, idx) => {
                        const pct = summary.usage > 0 ? ((item.amount / summary.usage) * 100).toFixed(1) : "0";
                        return (
                          <MDBox key={item.service} display="flex" alignItems="center" gap={1} py={0.4}>
                            <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS[idx % COLORS.length], flexShrink: 0 }} />
                            <MDTypography variant="caption" color="dark" sx={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.service}
                            </MDTypography>
                            <MDTypography variant="caption" color="secondary" sx={{ fontSize: 10, whiteSpace: "nowrap" }}>
                              {fmt(item.amount)} ({pct}%)
                            </MDTypography>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              {/* Top Services Bar */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", border: "1px solid #E8EDF2", borderRadius: "16px" }}>
                  <MDBox px={2.5} py={2}>
                    <MDTypography variant="h6" fontWeight="bold" color="dark">Top 10 Service Costs</MDTypography>
                  </MDBox>
                  <Divider sx={{ m: 0 }} />
                  <MDBox p={2} sx={{ height: 320 }}>
                    {barCfg && <Bar data={barCfg.data} options={barCfg.options} />}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            {/* Resource Bar Chart */}
            {top15Resources.length > 0 && (
              <Card sx={{ border: "1px solid #E8EDF2", borderRadius: "16px", mb: 4 }}>
                <MDBox px={2.5} py={2}>
                  <MDTypography variant="h6" fontWeight="bold" color="dark">Top 15 Resource Costs</MDTypography>
                  {meta && (
                    <MDTypography variant="caption" color="secondary">
                      {meta.rangeUsed?.start} — {meta.rangeUsed?.end}{meta.truncated ? " (truncated)" : ""}
                    </MDTypography>
                  )}
                </MDBox>
                <Divider sx={{ m: 0 }} />
                <MDBox p={2} sx={{ height: 440 }}>
                  {resCfg && <Bar data={resCfg.data} options={resCfg.options} />}
                </MDBox>
              </Card>
            )}

            {/* Service Table */}
            <Card sx={{ border: "1px solid #E8EDF2", borderRadius: "16px", mb: 4 }}>
              <MDBox px={2.5} py={2}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">Service Cost Breakdown</MDTypography>
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <DataTable
                table={svcTable}
                isSorted canSearch showTotalEntries
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 25] }}
                noEndBorder
              />
            </Card>

            {/* Resource Table */}
            {resources.filter((r) => r.amount > 0).length > 0 && (
              <Card sx={{ border: "1px solid #E8EDF2", borderRadius: "16px", mb: 3 }}>
                <MDBox px={2.5} py={2}>
                  <MDTypography variant="h6" fontWeight="bold" color="dark">Resource Cost Details</MDTypography>
                </MDBox>
                <Divider sx={{ m: 0 }} />
                <DataTable
                  table={resTable}
                  isSorted canSearch showTotalEntries
                  entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 25] }}
                  noEndBorder
                />
              </Card>
            )}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CostBilling;
